import React, { useState } from 'react';
import { Avatar, IconButton, Tab, Tabs } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { USER } from '../../../../constants/CommonConst';
import './SentMessageView.scss';

const getLoggedInUser = () => {
  try { return decryptLocalStorage(USER) || {}; } catch { return {}; }
};

const FieldView = ({ label, children }) => (
  <div className='smv-field'>
    <div className='smv-label'>{label}</div>
    <div className='smv-value'>{children}</div>
  </div>
);

const PillList = ({ items }) => {
  if (!items || items.length === 0) return <span className='smv-value'>—</span>;
  return (
    <div className='smv-pill-list'>
      {items.map((item, idx) => (
        <span key={idx} className='smv-pill'>{item}</span>
      ))}
    </div>
  );
};

const CreatedByFooter = ({ message }) => {
  const loggedUser = getLoggedInUser();
  const name = message?.createdBy || `${loggedUser?.firstName || ''} ${loggedUser?.lastName || ''}`.trim() || 'User';
  const imageUrl = message?.createdBy ? null : loggedUser?.imageUrl;

  return (
    <div className='smv-created-by'>
      <span className='smv-created-by-label'>Created By</span>
      <div className='smv-created-by-user'>
        <span className='smv-created-by-name'>{name}</span>
        {imageUrl ? (
          <Avatar src={imageUrl} alt={name} className='smv-avatar' style={{ width: 32, height: 32 }} />
        ) : (
          <Avatar className='smv-avatar' style={{ width: 32, height: 32, fontSize: 14 }}>
            {name?.[0]?.toUpperCase()}
          </Avatar>
        )}
      </div>
    </div>
  );
};

const TemplateTab = ({ message }) => {
  const bodyHtml = message?.subjectBody || message?.body || '';
  const isHtml = bodyHtml.trimStart().startsWith('<');
  return (
    <div className='smv-tab-body'>
      {message?.fromAddress && <FieldView label='From Address'>{message.fromAddress}</FieldView>}
      {message?.cc && <FieldView label='CC'>{message.cc}</FieldView>}
      {message?.subject && <FieldView label='Subject Line'>{message.subject}</FieldView>}
      {bodyHtml && (
        <FieldView label='Subject Body'>
          {isHtml ? (
            <div
              className='smv-body-html'
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : (
            bodyHtml
          )}
        </FieldView>
      )}
    </div>
  );
};


const AudienceTab = ({ message }) => {
  const audienceType = message?.audience?.type;
  const isGuests = audienceType === 'GUEST_SEGMENTS' || message?.recipientMode === 'Guest Segments';
  const audienceLabel = isGuests ? 'Guest Segments' : 'Custom';

  const toEmails =
    message?.toEmails ||
    (Array.isArray(message?.audience?.emails)
      ? message.audience.emails.join(', ')
      : message?.audience?.emails || '');

  const guestSegments = message?.audience?.guestSegments || {};
  const blockCodes = guestSegments?.blockCodes || [];
  const loyaltyTiers = guestSegments?.loyaltyTiers || [];
  const rooms = guestSegments?.rooms || [];
  const roomTypes = guestSegments?.roomTypes || [];

  const segmentTypeLabel =
    blockCodes.length > 0 ? 'Block Codes' :
    loyaltyTiers.length > 0 ? 'Loyalty Tiers' :
    rooms.length > 0 ? 'Rooms' :
    roomTypes.length > 0 ? 'Room Types' : '';

  if (!isGuests) {
    return (
      <div className='smv-tab-body'>
        <FieldView label='Audience'>{audienceLabel}</FieldView>
        {toEmails && <FieldView label='Email Addresses'>{toEmails}</FieldView>}
      </div>
    );
  }

  return (
    <div className='smv-tab-body'>
      <div className='smv-two-col'>
        <FieldView label='Audience'>{audienceLabel}</FieldView>
        {segmentTypeLabel && <FieldView label='Audience Type'>{segmentTypeLabel}</FieldView>}
      </div>
      {blockCodes.length > 0 && (
        <FieldView label='Block Codes'>
          <PillList items={blockCodes} />
        </FieldView>
      )}
      {loyaltyTiers.length > 0 && (
        <FieldView label='Loyalty Tiers'>
          <PillList items={loyaltyTiers} />
        </FieldView>
      )}
      {rooms.length > 0 && (
        <FieldView label='Rooms'>
          <PillList items={rooms} />
        </FieldView>
      )}
      {roomTypes.length > 0 && (
        <FieldView label='Room Types'>
          <PillList items={roomTypes} />
        </FieldView>
      )}
    </div>
  );
};

const SentMessageView = ({ message, onClose }) => {
  const [tabValue, setTabValue] = useState(0);

  return (
    <div className='category-create' style={{ width: '1000px' }}>
      <div className='container d-flex align-items-center flex-column service-container'>

        {/* Header row */}
        <div className='row px-3 w-100 mt-4 align-items-center'>
          <div className='col-auto d-flex align-items-center'>
            <span className='page-title me-3'>View Message</span>
          </div>
          <div className='col d-flex align-items-center justify-content-end gap-3'>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              indicatorColor='primary'
              textColor='primary'
              className='property-tabs'
              aria-label='view-message-tabs'
            >
              <Tab label='Template' />
              <Tab label='Audience' />
            </Tabs>
            <IconButton aria-label='close' onClick={onClose} className='ms-2'>
              <CancelIcon />
            </IconButton>
          </div>
        </div>

        <div className='row px-3 w-100 d-flex flex-grow-1 mt-4'>
          {tabValue === 0 && <TemplateTab message={message} />}
          {tabValue === 1 && <AudienceTab message={message} />}
        </div>

        <CreatedByFooter message={message} />
      </div>
    </div>
  );
};

export default SentMessageView;
